// OWNER: LG-ADMIN. §10 tab 2: niche + version, offer to sort by, geography from GET /geo (open / hand-pick / closed),
// cities or a whole state, terms, per-term limit, mode; Estimate (creates the draft, then POST /estimate) fills the
// panel; Run asks "Spend up to $X on … (Mode)?" and sends {budgetUsd, confirmHandPick}. 409 search_blocked lists why.
import { useEffect, useMemo, useState } from 'preact/hooks';
import type { Country } from '../../../src/shared/api';
import {
  LEAD_OFFERS, type LeadOffer, type LeadSearch, type NicheProfileRow, type SearchEstimate, type SearchMode,
} from '../../../src/shared/api-leadgen';
import { Modal, toast } from '../components/ui';
import { ApiErrorBox, Check, Field, ProblemList, inputValue, useLoad } from '../admin/ui';
import { apiErrorView, type ApiErrorView } from '../admin/errors';
import { leadgenApi } from './endpoints';
import {
  CRTC_SENTENCE, FULL_DEFAULT_LIMIT, HAND_PICK_CAP_DEFAULT, MODE_RULES, OFFER_LABELS, areasSummary, blockersFromError, budgetProblems,
  canadaHealthcareBlocked, defaultNiche, defaultOffer, fixedBudgetFor, geoChoices, int, newSearchProblems, nicheChoices, nicheOptionGroups,
  nicheReadiness, NO_ACTIVE_NICHE, parseNewSearchPrefill,
  regionWord, requestSignature, runConfirmText, toCreateRequest, unitemisedUsd, unitsText, usd, usd2, vendorLabel, type AreaPick, type GeoChoice, type NewSearchForm,
} from './logic';
import { Blockers, Meter, Money, ToneChip } from './parts';
import { useMe } from '../me';
import { refreshApprovals, useApprovals } from '../approvals';

type Mode = Exclude<SearchMode, 'single'>;
const MODES: Mode[] = ['smoke', 'pilot', 'full'];
const nicheId = (r: NicheProfileRow) => `${r.key}@${r.version}`;

export function NewSearchTab(props: { onCreated: (search: LeadSearch) => void }) {
  const me = useMe();
  const appr = useApprovals();
  // SPEC §19: setters start Full searches only (smoke and pilot calibrate a niche: admin work); anyone but the spend
  // owner sends the run to the owner for approval instead of spending.
  const modes: Mode[] = me.role === 'admin' ? MODES : ['full'];
  const needsApproval = !appr || !appr.isOwner;
  const ownerFirst = (appr?.ownerName ?? 'PJ').split(' ')[0];
  const niches = useLoad(() => leadgenApi.niches(), []);
  const [nid, setNid] = useState('');
  const [mode, setMode] = useState<Mode>(me.role === 'admin' ? 'smoke' : 'full');
  const [offer, setOffer] = useState<LeadOffer>('front_desk');
  const [country, setCountry] = useState<Country>('US');
  const [picks, setPicks] = useState<AreaPick[]>([]);
  const [terms, setTerms] = useState<string[]>([]);
  const [limit, setLimit] = useState(FULL_DEFAULT_LIMIT);
  const [handPickOk, setHandPickOk] = useState(false);

  const [draft, setDraft] = useState<{ search: LeadSearch; sig: string } | null>(null);
  const [estimate, setEstimate] = useState<SearchEstimate | null>(null);
  const [budgetText, setBudgetText] = useState('');
  const [busy, setBusy] = useState<'' | 'estimate' | 'run'>('');
  const [error, setError] = useState<ApiErrorView | null>(null);
  const [blockers, setBlockers] = useState<string[]>([]);
  const [confirming, setConfirming] = useState(false);

  const choices = useMemo(() => nicheChoices(niches.data ?? []), [niches.data]);
  // P0-7: grouped by readiness; setters see drafts not at all and non-runnable niches disabled.
  const optionGroups = useMemo(() => nicheOptionGroups(niches.data ?? [], mode, me.role === 'admin'), [niches.data, mode, me.role]);
  const noneReady = !!niches.data && me.role !== 'admin' && !choices.some((r) => r.status === 'active');
  const niche = choices.find((r) => nicheId(r) === nid) ?? null;
  const profile = niche?.profile ?? null;

  // pre-fill once: /lead-search/new?niche=&v=&mode= (from Niches), else the newest active/approved profile
  const [prefilled, setPrefilled] = useState(false);
  useEffect(() => {
    if (prefilled || !niches.data) return;
    const pre = parseNewSearchPrefill(location.search);
    const pick = defaultNiche(choices, pre.niche, pre.version);
    if (pick) selectNiche(pick);
    if (pre.mode && modes.includes(pre.mode)) changeMode(pre.mode, pick);
    setPrefilled(true);
  }, [niches.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectNiche = (r: NicheProfileRow | null) => {
    setNid(r ? nicheId(r) : '');
    setTerms(r ? [...r.profile.search_terms] : []);
    setOffer(defaultOffer(r?.profile ?? null));
  };
  const changeMode = (m: Mode, r: NicheProfileRow | null = niche) => {
    setMode(m);
    if (MODE_RULES[m].allTerms && r) setTerms([...r.profile.search_terms]);
    if (MODE_RULES[m].oneCity) setPicks((ps) => ps.slice(0, 1).map((p) => ({ ...p, whole: false })));
  };
  const changeCountry = (c: Country) => { setCountry(c); setPicks([]); setHandPickOk(false); };

  const geo = useLoad(() => leadgenApi.geo(country), [country]);
  const groups = useMemo(() => geoChoices(geo.data ?? [], profile), [geo.data, profile]);
  // §14: a list-health breach blocks the next Full search in the niche, with the reason shown here
  const health = useLoad(async () => (niche && mode === 'full' && me.role === 'admin' ? leadgenApi.calibration(niche.key) : null), [niche?.key, mode]);
  const breaches = health.data?.listHealth?.breaches ?? [];

  const form: NewSearchForm = { niche, mode, offer, country, picks, terms, limitPerTerm: limit, handPickConfirmed: handPickOk };
  const problems = newSearchProblems(form, geo.data, breaches);
  const req = toCreateRequest(form, geo.data, breaches);
  const sig = requestSignature(req);
  const stale = !!estimate && !!draft && draft.sig !== sig;
  const rules = MODE_RULES[mode];
  const budget = Number(budgetText);
  // P0-3: a pilot's fixed budget is sized to its terms by the server (stored on the draft it created).
  const fixedBudget = fixedBudgetFor(mode, draft?.search ?? null);
  const budgetIssues = estimate && !stale ? budgetProblems(budget, estimate, mode, fixedBudget) : [];
  const handPicked = picks.filter((p) => groups.handPick.some((c) => c.option.code === p.code));
  const canRun = !!req && !!draft && !!estimate && !stale && estimate.blockers.length === 0 && !budgetIssues.some((p) => p.level === 'error') && !busy;

  const togglePick = (c: GeoChoice, on: boolean) => {
    setPicks((ps) => {
      if (!on) return ps.filter((p) => p.code !== c.option.code);
      const next = { code: c.option.code, name: c.option.name, whole: false, citiesText: '' };
      return rules.oneCity ? [next] : [...ps.filter((p) => p.code !== c.option.code), next];
    });
  };
  const updatePick = (code: string, patch: Partial<AreaPick>) => setPicks((ps) => ps.map((p) => (p.code === code ? { ...p, ...patch } : p)));

  const doEstimate = async () => {
    if (!req) return;
    setBusy('estimate'); setError(null); setBlockers([]);
    try {
      let d = draft;
      if (!d || d.sig !== sig) { d = { search: await leadgenApi.createSearch(req), sig }; setDraft(d); }
      const est = await leadgenApi.estimate(d.search.id);
      setEstimate(est);
      setBudgetText((fixedBudgetFor(mode, d.search) ?? est.suggestedBudgetUsd).toFixed(2));
    } catch (e) { setError(apiErrorView(e)); setBlockers(blockersFromError(e)); } finally { setBusy(''); }
  };
  const doRun = async () => {
    if (!draft) return;
    setBusy('run'); setError(null); setBlockers([]);
    try {
      const r = await leadgenApi.run(draft.search.id, { budgetUsd: budget, confirmHandPick: handPicked.length > 0 && handPickOk });
      if ('message' in r) {
        toast(r.message, 'info');
        void refreshApprovals();
        setConfirming(false); const created = draft.search; setDraft(null); setEstimate(null);
        props.onCreated(created);
        return;
      }
      toast(`Search #${r.id} is running (budget ${usd2(r.budgetUsd)}).`, 'ok');
      setConfirming(false); setDraft(null); setEstimate(null);
      props.onCreated(r);
    } catch (e) { setConfirming(false); setError(apiErrorView(e)); setBlockers(blockersFromError(e)); } finally { setBusy(''); }
  };

  const geoItem = (c: GeoChoice) => {
    const picked = picks.some((p) => p.code === c.option.code);
    return (
      <div class={`lg-geo-item${picked ? ' picked' : ''}${c.selectable ? '' : ' off'}`}>
        <Check checked={picked} disabled={!c.selectable} onChange={(v) => togglePick(c, v)} label={c.option.name} title={c.reason ?? undefined} />
        {c.reason ? <span class="lg-geo-why">{c.reason}</span> : null}
        {c.handPick && c.option.setterNote ? <span class="lg-geo-note">{c.option.setterNote}</span> : null}
      </div>
    );
  };

  return (
    <div class="lg-grid-2">
      <div class="stack" style="gap:14px">
        <div class="card stack">
          <h3 style="margin:0">What</h3>
          <div class="adm-grid">
            <Field label="Niche" hint={niche ? nicheReadiness(niche, mode).line : undefined}>
              <select value={nid} onChange={(e) => selectNiche(choices.find((r) => nicheId(r) === inputValue(e)) ?? null)}>
                <option value="">{niches.loading && !niches.data ? 'Loading…' : 'Pick a niche…'}</option>
                {optionGroups.map((g) => (
                  <optgroup label={g.label}>
                    {g.options.map((o) => <option value={nicheId(o.row)} disabled={o.disabled} title={o.readiness.line}>{o.label}</option>)}
                  </optgroup>
                ))}
              </select>
            </Field>
          </div>
          {noneReady ? <div class="chip chip-warn" style="display:block;white-space:normal;padding:6px 8px">{NO_ACTIVE_NICHE}</div> : null}
          <details class="lg-advanced">
            <summary class="lg-note">Advanced</summary>
            <Field label="Sort results by offer" hint="Only changes the sort and who gets people research; all three scores are computed.">
              <select value={offer} onChange={(e) => setOffer(inputValue(e) as LeadOffer)}>
                {(profile?.offers_ranked.map((o) => o.offer) ?? []).concat(LEAD_OFFERS.filter((o) => !profile?.offers_ranked.some((x) => x.offer === o)))
                  .map((o) => <option value={o}>{OFFER_LABELS[o]}</option>)}
              </select>
            </Field>
          </details>
          <div class="stack" style="gap:4px">
            <span class="eyebrow">Mode</span>
            <div class="row" role="radiogroup" aria-label="Mode">
              {modes.map((m) => (
                <button type="button" role="radio" aria-checked={mode === m} class={`btn btn-sm ${mode === m ? 'btn-ink' : ''}`} onClick={() => changeMode(m)}>{MODE_RULES[m].label}</button>
              ))}
            </div>
            <span class="lg-note">{rules.hint}</span>
          </div>
          {niches.error ? <ApiErrorBox error={{ status: null, code: null, message: niches.error, problems: [], extra: [] }} /> : null}
        </div>

        <div class="card stack">
          <div class="lg-card-head">
            <h3>Where</h3>
            <select value={country} onChange={(e) => changeCountry(inputValue(e) as Country)} aria-label="Country"><option value="US">US</option><option value="CA">Canada</option></select>
          </div>
          {canadaHealthcareBlocked(niche, country) ? <div class="chip chip-bad" style="display:block;padding:8px 10px;white-space:normal">{CRTC_SENTENCE}</div> : null}
          {geo.error ? <ApiErrorBox error={{ status: null, code: null, message: geo.error, problems: [], extra: [] }} /> : null}
          {geo.loading && !geo.data ? <div class="muted">Loading {regionWord(country)} rules…</div> : null}
          {geo.data ? (
            <>
              <div class="lg-geo-group">
                <span class="eyebrow">Open <span class="chip">{groups.open.length}</span></span>
                <div class="lg-geo">{groups.open.map(geoItem)}</div>
              </div>
              {groups.handPick.length ? (
                <div class="lg-geo-group">
                  <span class="eyebrow">Caution: hand-pick only <span class="chip chip-warn">{groups.handPick.length}</span></span>
                  <div class="lg-geo">{groups.handPick.map(geoItem)}</div>
                  {handPicked.length ? (
                    <Check checked={handPickOk} onChange={setHandPickOk}
                      label={`Leads here are hand-pick only; at most ${HAND_PICK_CAP_DEFAULT} per state import`} />
                  ) : null}
                </div>
              ) : null}
              {groups.closed.length ? (
                <details class="lg-closed">
                  <summary>Closed: {groups.closed.length} (not selectable)</summary>
                  <div class="lg-geo" style="margin-top:6px">{groups.closed.map(geoItem)}</div>
                </details>
              ) : null}
            </>
          ) : null}
          {picks.length ? (
            <div>
              {picks.map((p) => (
                <div class="lg-area">
                  <div class="stack" style="gap:4px">
                    <b>{p.name}</b>
                    <Check checked={!p.whole} onChange={(v) => updatePick(p.code, { whole: !v })} label="Cities" />
                    <Check checked={p.whole} disabled={rules.oneCity} onChange={(v) => updatePick(p.code, { whole: v })}
                      label={`Whole ${regionWord(country)}`} title={rules.oneCity ? `A ${rules.label.toLowerCase()} runs on one city.` : undefined} />
                  </div>
                  {p.whole ? <div class="lg-note">The whole {regionWord(country)}: one Maps search per term over all of it (DataForSEO returns Google's top places, up to the per-term limit; Apify splits it into smaller searches). List cities for deeper coverage.</div> : (
                    <textarea placeholder={rules.oneCity ? 'One city, e.g. Hamilton' : 'One city per line'} value={p.citiesText}
                      aria-label={`Cities in ${p.name}`} onInput={(e) => updatePick(p.code, { citiesText: inputValue(e) })} />
                  )}
                </div>
              ))}
            </div>
          ) : null}
        </div>

        <div class="card stack">
          <h3 style="margin:0">Terms and size</h3>
          {profile ? (
            <div class="lg-chips" role="group" aria-label="Search terms">
              {profile.search_terms.map((t) => {
                const on = terms.includes(t);
                return (
                  <button type="button" class="chip lg-chip-toggle" aria-pressed={on} disabled={rules.allTerms}
                    onClick={() => setTerms((cur) => (on ? cur.filter((x) => x !== t) : [...cur, t]))}>{t}</button>
                );
              })}
            </div>
          ) : <div class="lg-note">Pick a niche to see its terms.</div>}
          {rules.allTerms ? <span class="lg-note">A {rules.label.toLowerCase()} uses every term.</span> : null}
          <Field label="Places per term, per area" hint={rules.limitPerTerm !== null ? `Fixed at ${rules.limitPerTerm} for a ${rules.label.toLowerCase()}.` : 'Maps results per search: DataForSEO depth (billed per 100), or Apify maxCrawledPlacesPerSearch.'}>
            <input type="number" min={1} step={1} style="max-width:140px" value={rules.limitPerTerm ?? limit} disabled={rules.limitPerTerm !== null}
              onInput={(e) => setLimit(Math.floor(Number(inputValue(e))))} />
          </Field>
        </div>
      </div>

      <div class="stack" style="gap:14px">
        <div class="card stack">
          <div class="lg-card-head">
            <h3>Estimate</h3>
            <button class="btn btn-ink" onClick={doEstimate} disabled={!req || busy !== ''}>{busy === 'estimate' ? 'Estimating…' : estimate && !stale ? 'Estimate again' : 'Estimate'}</button>
          </div>
          {req ? <div class="lg-note">{niche!.displayName} v{niche!.version} · {areasSummary(req.areas)} · {req.terms.length} terms × {req.limitPerTerm} · {MODE_RULES[mode].label}</div> : null}
          <ProblemList problems={problems} />
          {mode === 'full' && health.error ? <div class="lg-note">List health couldn't be read ({health.error}). The server still checks it when you run.</div> : null}
          <ApiErrorBox error={error} />
          <Blockers items={blockers.filter((b) => !error || !error.message.includes(b))} />
          {estimate ? <EstimatePanel est={estimate} stale={stale} /> : <div class="lg-note">The estimate creates a draft search and prices it. Nothing is spent until you run it.</div>}
        </div>

        {estimate && !stale ? (
          <div class="card stack">
            <h3 style="margin:0">Budget and run</h3>
            <Field label="Search budget (USD)" hint={fixedBudget !== null ? `Fixed at ${usd2(fixedBudget)} for a ${rules.label.toLowerCase()}${mode === 'pilot' ? ` (sized to its ${req?.terms.length ?? 0} terms)` : ''}.` : `Suggested ${usd2(estimate.suggestedBudgetUsd)} (estimate × 1.3). Every paid call is reserved against it first.`}>
              <input type="number" min={0.01} step={0.01} style="max-width:160px" value={budgetText} disabled={fixedBudget !== null}
                onInput={(e) => setBudgetText(inputValue(e))} />
            </Field>
            <ProblemList problems={budgetIssues} />
            <div class="row" style="justify-content:flex-end">
              <button class="btn btn-primary" disabled={!canRun} onClick={() => setConfirming(true)}>{needsApproval ? `Send to ${ownerFirst} for approval…` : 'Run…'}</button>
            </div>
          </div>
        ) : null}
      </div>

      {confirming && req && niche ? (
        <Modal title={needsApproval ? `Ask ${ownerFirst} to approve this search?` : 'Run this search?'} onClose={() => (busy ? null : setConfirming(false))}>
          <div class="stack">
            <div style="font-size:16px">{runConfirmText({ budgetUsd: budget, nicheName: niche.displayName, areas: req.areas, mode })}</div>
            <div class="lg-note">Search #{draft?.search.id}. It stops at {usd2(budget)} (paused, with an admin task) and never passes the month cap.</div>
            {needsApproval ? <div class="chip chip-info" style="display:block;white-space:normal;padding:6px 8px">Nothing is spent yet: {ownerFirst} gets this request by email, and the search starts only when {ownerFirst} approves it (possibly with a lower budget). You'll get an email either way.</div> : null}
            {handPicked.length ? <div class="chip chip-warn" style="display:block;white-space:normal;padding:6px 8px">Includes hand-pick {handPicked.map((p) => p.name).join(', ')}: at most {HAND_PICK_CAP_DEFAULT} per state import.</div> : null}
            <div class="row" style="justify-content:flex-end">
              <button class="btn" onClick={() => setConfirming(false)} disabled={busy !== ''}>Cancel</button>
              <button class="btn btn-primary" onClick={doRun} disabled={busy !== ''}>{busy === 'run' ? (needsApproval ? 'Sending…' : 'Starting…') : needsApproval ? `Ask to spend up to ${usd2(budget)}` : `Spend up to ${usd2(budget)}`}</button>
            </div>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}

function EstimatePanel(props: { est: SearchEstimate; stale: boolean }) {
  const e = props.est;
  const after = e.monthSpentUsd + e.totalUsd;
  const cap = e.monthCapUsd || 0;
  return (
    <div class={`stack${props.stale ? ' lg-stale' : ''}`} style="gap:10px">
      {props.stale ? <div class="chip chip-warn" style="display:block;padding:6px 8px">The form changed after this estimate. Estimate again before running.</div> : null}
      <div class="lg-est-nums">
        <div class="adm-stat"><b>{int(e.placesLow)}–{int(e.placesHigh)}</b><span>places (estimate)</span></div>
        <div class="adm-stat"><b>{int(e.survivorsExpected)}</b><span>expected survivors (estimate)</span></div>
        <div class="adm-stat"><b>{usd(e.totalUsd)}</b><span>estimated total</span></div>
        <div class="adm-stat"><b>{usd2(e.suggestedBudgetUsd)}</b><span>suggested budget</span></div>
      </div>
      <div class="lg-note">
        {e.probe === 'places_ids' ? 'Place counts come from a free Google IDs-only probe.' : 'No probe ran (no Places key, or too many probes needed): the counts come from the per-term caps only.'}
        {' '}Survival and hit rates are starting assumptions until this niche’s pilot measures them.
      </div>
      <div class="adm-table-wrap">
        <table>
          <thead><tr><th>Vendor</th><th>What</th><th class="num">Units</th><th class="num">USD</th></tr></thead>
          <tbody>
            {e.byVendor.map((v) => { const u = unitsText(v.op, v.units); return <tr><td>{vendorLabel(v.vendor)}</td><td class="lg-mono">{v.op}</td><td class="num" title={u.title}>{u.text}</td><td class="num"><Money usd={v.usd} /></td></tr>; })}
            {unitemisedUsd(e) > 0 ? (
              <tr><td colSpan={3} class="lg-note" title="The server left out lines whose units round to 0; their cost is still in the total.">Not itemised (lines too small to count)</td><td class="num"><Money usd={unitemisedUsd(e)} /></td></tr>
            ) : null}
            <tr class="adm-total"><td colSpan={3}>Total</td><td class="num"><Money usd={e.totalUsd} /></td></tr>
          </tbody>
        </table>
      </div>
      <div class="stack" style="gap:4px">
        <div class="row" style="justify-content:space-between"><span class="eyebrow">This month</span>
          <span class="lg-mono">{usd(e.monthSpentUsd)} spent + {usd(e.totalUsd)} = {usd(after)} of {usd2(cap)}</span></div>
        <Meter value={cap > 0 ? after / cap : null} tone={cap > 0 && after > cap ? 'over' : cap > 0 && after > cap * 0.8 ? 'warn' : ''} label="Month to date plus this estimate, against the cap" />
      </div>
      {e.blockers.length ? (
        <div class="stack" style="gap:4px">
          <span class="eyebrow">Can't run yet</span>
          <Blockers items={e.blockers} />
        </div>
      ) : <ToneChip tone="ok">No blockers</ToneChip>}
    </div>
  );
}
